import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalysis } from '../context/AnalysisContext';
import useAnalysisStore from '../stores/useAnalysisStore';
import { useAuth } from '../context/AuthContext';
import { useReadOnly } from '../context/ReadOnlyContext';
import { generateDefaultLevels, LEVEL_COLOR_CELL, LEVEL_COLOR_BG, LEVEL_COLOR_TEXT } from './common/RiskLevelsDisplay';
import { GRAVITY_LEVELS } from '../constants/gravityLevels';
import {
  Plus,
  FileText,
  FolderOpen,
  RefreshCw,
  Clock,
  Edit2,
  Trash2,
  Calendar,
  BarChart,
  ChevronDown,
  ChevronRight,
  Briefcase,
  X,
  Shield,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Eye,
  Check,
  Info,
  History,
  Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import VersionHistoryPanel from './common/VersionHistoryPanel';
import VersionSnapshotModal from './common/VersionSnapshotModal';
import StrategicSaveModal from './common/StrategicSaveModal';

/**
 * Enhanced Analysis Selector with history and detailed information
 */
const AnalysisSelector = ({ hideHeader = false }) => {
  const { t, i18n } = useTranslation();
  const { selectAnalysis } = useAnalysis();
  const analysisStore = useAnalysisStore();
  const currentAnalysis = analysisStore.currentAnalysis;
  const analyses = analysisStore.analyses;
  const isLoading = analysisStore.isLoading;
  const createAnalysis = useAnalysisStore.getState().createAnalysis;
  const updateAnalysis = useAnalysisStore.getState().updateAnalysis;
  const deleteAnalysis = useAnalysisStore.getState().deleteAnalysis;

  const { user, isAdmin, isAuthenticated } = useAuth();

  const loadAnalyses = async (page = 1, limit = 10) => {
    return useAnalysisStore.getState().loadAnalyses(user, isAuthenticated, page, limit);
  };
  const { isViewer, checkAnalysisPermission } = useReadOnly();

  const syncGravityLevels = (current, count) =>
    Array.from({ length: count }, (_, i) => ({ ...(current[i] || GRAVITY_LEVELS[i]) }));

  // Local state
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(null); // State for which analysis is being edited
  const [showDetails, setShowDetails] = useState(false);
  const [newAnalysisName, setNewAnalysisName] = useState('');
  const [newAnalysisDescription, setNewAnalysisDescription] = useState('');
  const [newAnalysisClassification, setNewAnalysisClassification] = useState('Confidentielle');
  const [newAnalysisRiskLevelsCount, setNewAnalysisRiskLevelsCount] = useState(4);
  const [newConfigLevelsCount, setNewConfigLevelsCount] = useState(4);
  const [newRiskLevels, setNewRiskLevels] = useState(() => generateDefaultLevels(4));
  const [newGravityLevels, setNewGravityLevels] = useState(() => syncGravityLevels(GRAVITY_LEVELS, 4));
  const [editAnalysisName, setEditAnalysisName] = useState('');
  const [editAnalysisDescription, setEditAnalysisDescription] = useState('');
  const [editAnalysisClassification, setEditAnalysisClassification] = useState('Confidentielle');
  const [editAnalysisRiskLevelsCount, setEditAnalysisRiskLevelsCount] = useState(4);
  const [editConfigLevelsCount, setEditConfigLevelsCount] = useState(4);
  const [editRiskLevels, setEditRiskLevels] = useState(() => generateDefaultLevels(4));
  const [editGravityLevels, setEditGravityLevels] = useState(() => syncGravityLevels(GRAVITY_LEVELS, 4));
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  const [localLoading, setLocalLoading] = useState(false);

  // Version History State
  const [historyAnalysis, setHistoryAnalysis] = useState(null);
  const [snapshotVersion, setSnapshotVersion] = useState(null);

  // Strategic Save State
  const [strategicAnalysis, setStrategicAnalysis] = useState(null);
  const [strategicSaving, setStrategicSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 3, total: 0, pages: 0 });
  const itemsPerPage = 3;

  // Load analyses on component mount with pagination
  useEffect(() => {
    const loadPaginatedAnalyses = async () => {
      const result = await loadAnalyses(currentPage, itemsPerPage);
      if (result) {
        setPagination(result.pagination);
        
        // If current analysis exists but is not in the current page, we might want to inform the user
        // or optionally we could try to find which page it's on (complex with server-side pagination)
        // For now, increasing the limit to 10 significantly reduces this issue.
      }
    };

    loadPaginatedAnalyses();
  }, [currentPage, currentAnalysis?.id]); // Also reload if selection changes from outside

  const handleCreate = async () => {
    if (!newAnalysisName.trim()) return;

    // Ensure we have company information from the user
    if (!user || !user.companyId || !user.companyName) {
      alert(t('analysisSelector.alerts.mustBeConnected'));
      return;
    }

    setLocalLoading(true);

    try {
      const result = await createAnalysis({
        name: newAnalysisName,
        description: newAnalysisDescription,
        status: 'draft',
        classification: newAnalysisClassification,
        riskLevelsCount: newAnalysisRiskLevelsCount,
        riskLevels: newRiskLevels,
        gravityLevels: newGravityLevels,
        companyId: user.companyId,
        companyName: user.companyName
      });

      if (result) {
        // Reset form
        setNewAnalysisName('');
        setNewAnalysisDescription('');
        setNewRiskLevels(generateDefaultLevels(4));
        setNewConfigLevelsCount(4);
        setNewAnalysisRiskLevelsCount(4);
        setNewGravityLevels(syncGravityLevels(GRAVITY_LEVELS, 4));
        setShowCreate(false);

        // Select the newly created analysis
        selectAnalysis(result.id);
      }
    } catch (error) {
      console.error('Error creating analysis:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleEditClick = (analysis) => {
    setShowEdit(analysis.id);
    setEditAnalysisName(analysis.name);
    setEditAnalysisDescription(analysis.description || '');
    setEditAnalysisClassification(analysis.classification || 'Confidentielle');
    const count = analysis.riskLevelsCount || 4;
    setEditAnalysisRiskLevelsCount(count);
    const savedLevels = analysis.riskLevels?.length ? analysis.riskLevels : generateDefaultLevels(count);
    setEditRiskLevels(savedLevels);
    setEditConfigLevelsCount(savedLevels.length);
    setEditGravityLevels(syncGravityLevels(analysis.gravityLevels?.length ? analysis.gravityLevels : GRAVITY_LEVELS, count));
  };

  // Handle edit save
  const handleEditSave = async (analysisId) => {
    if (!editAnalysisName.trim()) return;

    setLocalLoading(true);

    try {
      const result = await updateAnalysis(analysisId, {
        name: editAnalysisName,
        description: editAnalysisDescription,
        classification: editAnalysisClassification,
        riskLevelsCount: editAnalysisRiskLevelsCount,
        riskLevels: editRiskLevels,
        gravityLevels: editGravityLevels
      });

      if (result.success) {
        setShowEdit(null);
        setEditAnalysisName('');
        setEditAnalysisDescription('');
        setEditAnalysisClassification('Confidentielle');
        setEditAnalysisRiskLevelsCount(4);
      }
    } catch (error) {
      console.error('Error updating analysis:', error);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleDeleteConfirm = async (id) => {
    if (!isAdmin) return;
    setLocalLoading(true);
    try {
      await deleteAnalysis(id);
    } finally {
      setLocalLoading(false);
      setDeleteConfirmation(null);
    }
  };

  // Handle strategic save (major version bump)
  const handleStrategicSave = async (analysis, justification) => {
    if (!isAdmin) return;
    setStrategicSaving(true);
    try {
      await updateAnalysis(analysis.id, {
        strategic: true,
        justification
      });
      // Refresh analyses list
      const result = await loadAnalyses(currentPage, itemsPerPage);
      if (result) setPagination(result.pagination);
    } catch (error) {
      console.error('Error performing strategic save:', error);
    } finally {
      setStrategicSaving(false);
      setStrategicAnalysis(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const locale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Determine if loading state should be shown
  const showLoading = isLoading || localLoading;

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${hideHeader ? 'p-0 border-none shadow-none' : 'p-4 mb-6'}`}>
      {!hideHeader && (
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-medium text-gray-800 flex items-center">
            <FileText size={18} className="mr-2 text-blue-500" />
            {t('analysisSelector.title')}
          </h3>

          <div className="flex space-x-2">
            <button
              className={`modern-btn ${showCreate
                ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                : 'modern-btn-primary'
                }`}
              onClick={() => setShowCreate(!showCreate)}
              disabled={showLoading}
            >
              {showCreate ? (
                <>
                  <X size={16} />
                  {t('analysisSelector.cancel')}
                </>
              ) : (
                <>
                  <Plus size={16} />
                  {t('analysisSelector.newAnalysis')}
                </>
              )}
            </button>

            <button
              className="px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={async () => {
                const result = await loadAnalyses(currentPage, itemsPerPage);
                if (result) {
                  setPagination(result.pagination);
                }
              }}
              disabled={showLoading}
            >
              <RefreshCw size={16} className={`${showLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -20 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -20 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="mb-8 overflow-hidden"
          >
            <div className="modern-card border-l-4 border-l-blue-500 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 border-b border-blue-100 flex justify-between items-start">
                <div>
                  <h4 className="text-lg font-bold text-blue-900 flex items-center">
                    {t('analysisSelector.newAnalysis')}
                  </h4>
                  <p className="text-sm text-blue-600/80 mt-1">
                    Créez une nouvelle analyse de risques pour votre organisation
                  </p>
                </div>
                <button
                  onClick={() => setShowCreate(false)}
                  className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white/50 rounded-full transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 bg-white space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('analysisSelector.analysisName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="modern-input w-full transition-all duration-200 focus:ring-4 focus:ring-blue-100"
                      value={newAnalysisName}
                      onChange={(e) => setNewAnalysisName(e.target.value)}
                      placeholder={t('analysisSelector.analysisNamePlaceholder')}
                      disabled={showLoading}
                      autoFocus
                    />
                  </div>

                  <div className="col-span-1 md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('analysisSelector.optionalDescription')}
                    </label>
                    <textarea
                      className="modern-input w-full min-h-[80px] transition-all duration-200 focus:ring-4 focus:ring-blue-100"
                      value={newAnalysisDescription}
                      onChange={(e) => setNewAnalysisDescription(e.target.value)}
                      placeholder={t('analysisSelector.descriptionPlaceholder')}
                      rows={3}
                      disabled={showLoading}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('analysisSelector.classification')}
                    </label>
                    <div className="relative">
                      <select
                        className="modern-input w-full appearance-none cursor-pointer"
                        value={newAnalysisClassification}
                        onChange={(e) => setNewAnalysisClassification(e.target.value)}
                        disabled={showLoading}
                      >
                        <option value="Confidentielle">{t('analysisSelector.classificationOptions.confidentielle')}</option>
                        <option value="Restreinte">{t('analysisSelector.classificationOptions.restreinte')}</option>
                        <option value="Sensible">{t('analysisSelector.classificationOptions.sensible')}</option>
                        <option value="Non protégée">{t('analysisSelector.classificationOptions.nonProtegee')}</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-gray-500">
                        <ChevronDown size={16} />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {t('analysisSelector.gravityLevels')}
                    </label>
                    <div className="flex gap-2">
                      {[4, 5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            setNewAnalysisRiskLevelsCount(n);
                            setNewGravityLevels(syncGravityLevels(newGravityLevels, n));
                          }}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            newAnalysisRiskLevelsCount === n
                              ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                          disabled={showLoading}
                        >
                          {n} {t('analysisSelector.levels', 'niveaux')}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{t('analysisSelector.gravityLevelsInfo')}</p>
                  </div>
                </div>

                {/* Gravity levels configuration */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <BarChart size={16} className="text-blue-500" />
                        {t('analysisSelector.gravityConfigTitle', 'Niveaux de gravité (échelle G1 - G5)')}
                      </h4>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">N°</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.label', 'Libellé')}</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.description', 'Description')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {newGravityLevels.map((g, idx) => (
                          <tr key={g.level} className="hover:bg-slate-50/50">
                            <td className="px-3 py-2 text-xs font-bold text-slate-700 w-8">{g.code || `G${g.level}`}</td>
                            <td className="px-3 py-2 text-xs font-medium text-slate-700 w-40">{g.label}</td>
                            <td className="px-3 py-2">
                              <textarea
                                value={g.description || ''}
                                onChange={e => setNewGravityLevels(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                                className="w-full min-w-[200px] text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-shadow resize-y"
                                rows="2"
                                placeholder={t('analysisSelector.riskLevelDescriptionPlaceholder', 'Décrivez ce niveau de gravité...')}
                                disabled={showLoading}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Risk levels configuration table */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <BarChart size={16} className="text-blue-500" />
                        {t('analysisSelector.riskLevelsConfig', 'Configuration des niveaux de risque et niveaux de gravité')}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 font-medium">{t('analysisSelector.configCount', 'Nbre de niveaux')}:</span>
                          <div className="flex gap-1">
                          {[3, 4].map(n => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => {
                                setNewConfigLevelsCount(n);
                                setNewRiskLevels(generateDefaultLevels(n));
                              }}
                              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                                newConfigLevelsCount === n
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                              }`}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">N°</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.label', 'Libellé')}</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.color', 'Couleur')}</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.threshold', 'Seuil')}</th>
                          <th className="text-center px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.acceptable', 'Acceptable')}</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.decision', 'ACCEPTABILITÉ DU RISQUE')}</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.description', 'Description')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {newRiskLevels.map((lv, idx) => (
                          <tr key={lv.level} className="hover:bg-slate-50/50">
                            <td className="px-3 py-2 text-xs font-bold text-slate-700 w-8">{lv.level}</td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={lv.label}
                                onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                                className="w-full min-w-[80px] text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-shadow"
                                disabled={showLoading}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex gap-1.5">
                                {['green', 'yellow', 'orange', 'red'].map(c => (
                                  <button
                                    key={c}
                                    type="button"
                                    onClick={() => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, color: c } : x))}
                                    className={`w-6 h-6 rounded-full border-2 transition-all ${
                                      lv.color === c ? 'border-blue-500 scale-110 shadow-sm' : 'border-transparent hover:scale-110'
                                    } ${LEVEL_COLOR_CELL[c]}`}
                                    title={c}
                                  />
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-1">
                                <input
                                  type="number" min="1"
                                  value={lv.minScore}
                                  onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, minScore: Number(e.target.value) } : x))}
                                  className="w-12 text-xs border border-slate-200 rounded-md px-1 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  disabled={showLoading}
                                />
                                <span className="text-xs text-slate-400 font-medium">-</span>
                                <input
                                  type="number" min="1"
                                  value={lv.maxScore}
                                  onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, maxScore: Number(e.target.value) } : x))}
                                  className="w-12 text-xs border border-slate-200 rounded-md px-1 py-1.5 text-center focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  disabled={showLoading}
                                />
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={lv.acceptable}
                                  onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, acceptable: e.target.checked } : x))}
                                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                  disabled={showLoading}
                                />
                              </label>
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="text"
                                value={lv.decision}
                                onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, decision: e.target.value } : x))}
                                className="w-full min-w-[120px] text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-shadow overflow-x-auto"
                                placeholder={t('analysisSelector.decisionPlaceholder', 'Actions...')}
                                disabled={showLoading}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <textarea
                                value={lv.description || ''}
                                onChange={e => setNewRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                                className="w-full min-w-[160px] text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-shadow resize-y"
                                rows="2"
                                placeholder={t('analysisSelector.riskLevelDescriptionPlaceholder', 'Décrivez ce niveau de risque...')}
                                disabled={showLoading}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Color preview strip */}
                  <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-500">{t('analysisSelector.preview', 'Aperçu')}:</span>
                    <div className="flex gap-2">
                      {newRiskLevels.map(lv => (
                        <span
                          key={lv.level}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${LEVEL_COLOR_BG[lv.color] || 'bg-green-100'} ${LEVEL_COLOR_TEXT[lv.color] || 'text-green-800'}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${LEVEL_COLOR_CELL[lv.color] || 'bg-green-500'}`} />
                          {lv.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-100 gap-3">
                  <button
                    className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-800 transition-all shadow-sm hover:shadow"
                    onClick={() => setShowCreate(false)}
                    disabled={showLoading}
                  >
                    {t('analysisSelector.cancel')}
                  </button>

                  <button
                    className={`modern-btn modern-btn-primary px-6 py-2.5 shadow-lg shadow-blue-200 ${!newAnalysisName.trim() || showLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:shadow-blue-300 hover:-translate-y-0.5'
                      }`}
                    onClick={handleCreate}
                    disabled={!newAnalysisName.trim() || showLoading}
                  >
                    {showLoading ? (
                      <span className="flex items-center">
                        <RefreshCw size={18} className="animate-spin mr-2" />
                        {t('analysisSelector.creating')}
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <Check size={18} className="mr-2" />
                        {t('analysisSelector.createAnalysis')}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mb-2">
        {/* Active Selection - Show if not on current page */}
        {currentAnalysis && !analyses.find(a => a.id === currentAnalysis.id) && (
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2 flex items-center">
              <Check size={12} className="mr-1" />
              Sélection active (Hormis liste)
            </h4>
            <div className="border rounded-md border-blue-300 bg-blue-50 transition-colors duration-200">
              <div className="px-3 py-2 flex justify-between items-center">
                <div className="flex items-center">
                  <div className="mr-2 text-blue-600">
                    <FileText size={16} />
                  </div>
                  <div>
                    <div className="font-medium text-blue-700 flex items-center">
                      {currentAnalysis.name}
                      {currentAnalysis.status === 'in-progress' && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-blue-100 text-blue-800 rounded-full">
                          {t('analysisSelector.status.inProgress')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center mt-0.5">
                      <span className="flex items-center">
                        <Calendar size={10} className="mr-1" />
                        {t('analysisSelector.modifiedOn')} {formatDate(currentAnalysis.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="px-2 py-1 rounded text-xs bg-blue-500 text-white">
                    {t('analysisSelector.selected')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {analyses.length > 0 ? (
          <>
            <div className="space-y-2">
              {analyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className={`border rounded-md ${currentAnalysis?.id === analysis.id
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/20'
                    } transition-colors duration-200`}
                >
                  <div className="px-3 py-2 flex justify-between items-center">
                    <div className="flex items-center">
                      <button
                        className="mr-2 focus:outline-none text-gray-500 hover:text-gray-700"
                        onClick={() => setExpandedAnalysis(expandedAnalysis === analysis.id ? null : analysis.id)}
                        disabled={showLoading}
                      >
                        {expandedAnalysis === analysis.id ?
                          <ChevronDown size={16} /> :
                          <ChevronRight size={16} />
                        }
                      </button>
                      <div>
                        <div
                          className={`font-medium flex items-center cursor-pointer ${currentAnalysis?.id === analysis.id ? 'text-blue-700' : 'text-gray-800'
                            }`}
                          onClick={() => {
                            if (!showLoading && currentAnalysis?.id !== analysis.id) {
                              selectAnalysis(analysis.id);
                            }
                          }}
                        >
                          {analysis.name}
                          {analysis.status === 'in-progress' && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-blue-100 text-blue-800 rounded-full">
                              {t('analysisSelector.status.inProgress')}
                            </span>
                          )}
                          {analysis.status === 'completed' && (
                            <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-green-100 text-green-800 rounded-full">
                              {t('analysisSelector.status.completed')}
                            </span>
                          )}
                          {(isViewer || checkAnalysisPermission(analysis.id)) ? (
                            <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-orange-100 text-orange-800 rounded-full flex items-center" title={t('common.readOnlyMessageAnalysis')}>
                              <Eye size={10} className="mr-1" />
                              {t('common.readOnlyBadge', 'Lecture seule')}
                            </span>
                          ) : (
                            <span className="ml-2 px-2 py-0.5 text-xs font-normal bg-indigo-100 text-indigo-800 rounded-full flex items-center" title={t('common.readWriteMessage')}>
                              <Edit2 size={10} className="mr-1" />
                              {t('common.readWriteBadge', 'Lecture-écriture')}
                            </span>
                          )}
                        </div>

                        <div className="text-xs text-gray-500 flex items-center mt-0.5 gap-3">
                          <span className="flex items-center">
                            <Calendar size={10} className="mr-1" />
                            {t('analysisSelector.modifiedOn')} {formatDate(analysis.updatedAt)}
                          </span>
                          <span className="flex items-center text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                            v{analysis.version || '1.0'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        className={`px-2 py-1 rounded text-sm ${showLoading ?
                          'bg-gray-300 text-gray-500 cursor-not-allowed' :
                          (currentAnalysis?.id === analysis.id
                            ? 'bg-blue-500 text-white'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200')
                          }`}
                        onClick={() => {
                          if (!showLoading && currentAnalysis?.id !== analysis.id) {
                            selectAnalysis(analysis.id);
                          }
                        }}
                        disabled={showLoading || currentAnalysis?.id === analysis.id}
                      >
                        {showLoading && currentAnalysis?.id !== analysis.id ?
                          <span className="flex items-center">
                            <RefreshCw size={12} className="animate-spin mr-1" />
                            {t('analysisSelector.loading')}
                          </span> :
                          (currentAnalysis?.id === analysis.id ? t('analysisSelector.selected') : t('analysisSelector.select'))
                        }
                      </button>

                      {/* History Button */}
                      <button
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title={t('versionManagement.history.buttonTooltip') || "Voir l'historique des versions"}
                        onClick={() => setHistoryAnalysis(analysis)}
                        disabled={showLoading}
                      >
                        <History size={16} />
                      </button>

                      {/* Strategic Save Button */}
                      {isAdmin && (
                        <button
                          className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title={t('versionManagement.strategicUpdate.buttonTooltip') || "Sauvegarde Stratégique — Créer une version majeure horodatée (snapshot complet) pour marquer un jalon important de l'analyse"}
                          onClick={() => setStrategicAnalysis(analysis)}
                          disabled={showLoading || strategicSaving}
                        >
                          <Save size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {expandedAnalysis === analysis.id && (
                    <div className="bg-gray-50 border-t border-gray-200 px-3 py-2">
                      {/* Edit form */}
                      {showEdit === analysis.id ? (
                        <div className="modern-card mb-4 border-l-4 border-l-amber-500 overflow-hidden shadow-md">
                          <div className="bg-amber-50 px-4 py-3 border-b border-amber-100 flex justify-between items-center">
                            <h5 className="font-bold text-amber-800 flex items-center gap-2">
                              <Edit2 size={16} />
                              {t('analysisSelector.editAnalysis')}
                            </h5>
                            <button
                              onClick={() => setShowEdit(null)}
                              className="text-amber-800/60 hover:text-amber-800 transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          <div className="p-4 bg-white space-y-3">
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">
                                {t('analysisSelector.analysisName')}
                              </label>
                              <input
                                type="text"
                                className="modern-input w-full text-sm"
                                value={editAnalysisName}
                                onChange={(e) => setEditAnalysisName(e.target.value)}
                                disabled={showLoading}
                                autoFocus
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">
                                {t('analysisSelector.description')}
                              </label>
                              <textarea
                                className="modern-input w-full text-sm min-h-[60px]"
                                value={editAnalysisDescription}
                                onChange={(e) => setEditAnalysisDescription(e.target.value)}
                                rows={2}
                                disabled={showLoading}
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1 uppercase tracking-wide">
                                {t('analysisSelector.classification')}
                              </label>
                              <div className="relative">
                                <select
                                  className="modern-input w-full text-sm appearance-none cursor-pointer"
                                  value={editAnalysisClassification}
                                  onChange={(e) => setEditAnalysisClassification(e.target.value)}
                                  disabled={showLoading}
                                >
                                  <option value="Confidentielle">{t('analysisSelector.classificationOptions.confidentielle')}</option>
                                  <option value="Restreinte">{t('analysisSelector.classificationOptions.restreinte')}</option>
                                  <option value="Sensible">{t('analysisSelector.classificationOptions.sensible')}</option>
                                  <option value="Non protégée">{t('analysisSelector.classificationOptions.nonProtegee')}</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-gray-500">
                                  <ChevronDown size={14} />
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
                                {t('analysisSelector.gravityLevels')}
                              </label>
                              <div className="flex gap-2">
                                {[4, 5].map(n => (
                                  <button
                                    key={n}
                                    type="button"
                                    onClick={() => {
                                      setEditAnalysisRiskLevelsCount(n);
                                      setEditGravityLevels(syncGravityLevels(editGravityLevels, n));
                                    }}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                      editAnalysisRiskLevelsCount === n
                                        ? 'bg-blue-600 text-white shadow-sm shadow-blue-200'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    disabled={showLoading}
                                  >
                                    {n} {t('analysisSelector.levels', 'niveaux')}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                              <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-blue-50 border-b border-slate-200">
                                <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                                  <BarChart size={16} className="text-blue-500" />
                                  {t('analysisSelector.gravityConfigTitle', 'Niveaux de gravité (échelle G1 - G5)')}
                                </h4>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">N°</th>
                                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.label', 'Libellé')}</th>
                                      <th className="text-left px-3 py-2.5 font-semibold text-slate-600 text-xs uppercase tracking-wider">{t('analysisSelector.description', 'Description')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {editGravityLevels.map((g, idx) => (
                                      <tr key={g.level} className="hover:bg-slate-50/50">
                                        <td className="px-3 py-2 text-xs font-bold text-slate-700 w-8">{g.code || `G${g.level}`}</td>
                                        <td className="px-3 py-2 text-xs font-medium text-slate-700 w-40">{g.label}</td>
                                        <td className="px-3 py-2">
                                          <textarea
                                            value={g.description || ''}
                                            onChange={e => setEditGravityLevels(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                                            className="w-full min-w-[200px] text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-shadow resize-y"
                                            rows="2"
                                            placeholder={t('analysisSelector.riskLevelDescriptionPlaceholder', 'Décrivez ce niveau de gravité...')}
                                            disabled={showLoading}
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Risk levels table */}
                            <div className="border border-slate-200 rounded-lg overflow-hidden">
                              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                                    {t('analysisSelector.riskLevelsConfig', 'Configuration des niveaux de risque et niveaux de gravité')}
                                  </span>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs text-slate-400">{t('analysisSelector.configCount', 'Nbre')}:</span>
                                    <div className="flex gap-0.5">
                                    {[3, 4].map(n => (
                                      <button
                                        key={n}
                                        type="button"
                                        onClick={() => {
                                          setEditConfigLevelsCount(n);
                                          setEditRiskLevels(generateDefaultLevels(n));
                                        }}
                                        className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                          editConfigLevelsCount === n
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                                        }`}
                                      >
                                        {n}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">N°</th>
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.label', 'Libellé')}</th>
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.color', 'Couleur')}</th>
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.threshold', 'Seuil')}</th>
                                      <th className="text-center px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.acceptable', 'Accept.')}</th>
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.decision', 'ACCEPTABILITÉ DU RISQUE')}</th>
                                      <th className="text-left px-2 py-1.5 font-semibold text-slate-500">{t('analysisSelector.description', 'Description')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-slate-100">
                                    {editRiskLevels.map((lv, idx) => (
                                      <tr key={lv.level} className="hover:bg-slate-50/50">
                                        <td className="px-2 py-1.5 font-bold text-slate-600">{lv.level}</td>
                                        <td className="px-2 py-1.5">
                                          <input
                                            type="text"
                                            value={lv.label}
                                            onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))}
                                            className="w-full min-w-[70px] border border-slate-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                            disabled={showLoading}
                                          />
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <div className="flex gap-1">
                                            {['green', 'yellow', 'orange', 'red'].map(c => (
                                              <button
                                                key={c}
                                                type="button"
                                                onClick={() => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, color: c } : x))}
                                                className={`w-4 h-4 rounded-full border-2 transition-all ${
                                                  lv.color === c ? 'border-blue-500 scale-110' : 'border-transparent'
                                                } ${LEVEL_COLOR_CELL[c]}`}
                                              />
                                            ))}
                                          </div>
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="number" min="1"
                                              value={lv.minScore}
                                              onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, minScore: Number(e.target.value) } : x))}
                                              className="w-10 border border-slate-200 rounded px-1 py-1 text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                                              disabled={showLoading}
                                            />
                                            <span className="text-slate-400">-</span>
                                            <input
                                              type="number" min="1"
                                              value={lv.maxScore}
                                              onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, maxScore: Number(e.target.value) } : x))}
                                              className="w-10 border border-slate-200 rounded px-1 py-1 text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                                              disabled={showLoading}
                                            />
                                          </div>
                                        </td>
                                        <td className="px-2 py-1.5 text-center">
                                          <input
                                            type="checkbox"
                                            checked={lv.acceptable}
                                            onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, acceptable: e.target.checked } : x))}
                                            className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                            disabled={showLoading}
                                          />
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <input
                                            type="text"
                                            value={lv.decision}
                                            onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, decision: e.target.value } : x))}
                                            className="w-full min-w-[100px] border border-slate-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 overflow-x-auto"
                                            placeholder="..."
                                            disabled={showLoading}
                                          />
                                        </td>
                                        <td className="px-2 py-1.5">
                                          <textarea
                                            value={lv.description || ''}
                                            onChange={e => setEditRiskLevels(prev => prev.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                                            className="w-full min-w-[120px] border border-slate-200 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-y"
                                            rows="2"
                                            placeholder={t('analysisSelector.riskLevelDescriptionPlaceholder', 'Décrivez ce niveau de risque...')}
                                            disabled={showLoading}
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
                                <span className="text-xs text-slate-400">{t('analysisSelector.preview', 'Aperçu')}:</span>
                                {editRiskLevels.map(lv => (
                                  <span
                                    key={lv.level}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${LEVEL_COLOR_BG[lv.color] || 'bg-green-100'} ${LEVEL_COLOR_TEXT[lv.color] || 'text-green-800'}`}
                                  >
                                    <span className={`w-1.5 h-1.5 rounded-full ${LEVEL_COLOR_CELL[lv.color] || 'bg-green-500'}`} />
                                    {lv.label}
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="flex justify-end pt-2 gap-2">
                              <button
                                className="px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                                onClick={() => setShowEdit(null)}
                                disabled={showLoading}
                              >
                                {t('analysisSelector.cancel')}
                              </button>
                              <button
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium text-white shadow-sm transition-all ${!editAnalysisName.trim() || showLoading
                                  ? 'bg-gray-300 cursor-not-allowed'
                                  : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md'
                                  }`}
                                onClick={() => handleEditSave(analysis.id)}
                                disabled={!editAnalysisName.trim() || showLoading}
                              >
                                {showLoading ? (
                                  <span className="flex items-center">
                                    <RefreshCw size={12} className="animate-spin mr-1.5" />
                                    {t('analysisSelector.saving')}
                                  </span>
                                ) : (
                                  <span className="flex items-center">
                                    <Check size={12} className="mr-1.5" />
                                    {t('analysisSelector.save')}
                                  </span>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-700 mb-2">
                          {analysis.description || <span className="italic text-gray-500">{t('analysisSelector.noDescription')}</span>}
                        </p>
                      )}

                      <div className="flex items-center text-xs text-gray-500 mb-2 gap-3">
                        <div className="flex items-center">
                          <Clock size={12} className="mr-1" />
                          {t('analysisSelector.createdOn')} {formatDate(analysis.createdAt)}
                        </div>
                        <div>
                          {t('analysisSelector.by')}: {analysis.createdByName || t('analysisSelector.unknownUser')}
                        </div>
                      </div>

                      <div className="flex justify-end mt-2">
                        <div className="flex gap-2">
                          <button
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center"
                            onClick={() => handleEditClick(analysis)}
                            disabled={showLoading}
                          >
                            <Edit2 size={12} className="mr-1" />
                            {t('analysisSelector.edit')}
                          </button>

                          {isAdmin && (
                            deleteConfirmation === analysis.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded"
                                  onClick={() => handleDeleteConfirm(analysis.id)}
                                  disabled={showLoading}
                                >
                                  {showLoading ? t('analysisSelector.deleting') : t('analysisSelector.confirm')}
                                </button>
                                <button
                                  className="text-xs bg-gray-200 hover:bg-gray-300 text-gray-700 px-2 py-1 rounded"
                                  onClick={() => setDeleteConfirmation(null)}
                                  disabled={showLoading}
                                >
                                  {t('analysisSelector.cancel')}
                                </button>
                              </div>
                            ) : (
                              <button
                                className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded flex items-center"
                                onClick={() => setDeleteConfirmation(analysis.id)}
                                disabled={showLoading}
                              >
                                <Trash2 size={12} className="mr-1" />
                                {t('analysisSelector.delete')}
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                <div className="flex items-center text-sm text-gray-500">
                  <span>
                    {t('analysisSelector.pagination.showing', { start: ((pagination.page - 1) * pagination.limit) + 1, end: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
                  </span>
                </div>

                <div className="flex items-center space-x-1">
                  {/* Previous Button */}
                  <button
                    className={`p-2 rounded-md ${pagination.page === 1 || showLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    onClick={() => !showLoading && pagination.page > 1 && setCurrentPage(pagination.page - 1)}
                    disabled={pagination.page === 1 || showLoading}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {/* Page Numbers */}
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    const pageNumber = Math.max(1, Math.min(pagination.pages - 4, pagination.page - 2)) + i;
                    if (pageNumber > pagination.pages) return null;

                    return (
                      <button
                        key={pageNumber}
                        className={`px-3 py-1 rounded-md text-sm ${pageNumber === pagination.page
                          ? 'bg-blue-500 text-white'
                          : 'text-gray-500 hover:bg-gray-100'
                          }`}
                        onClick={() => !showLoading && setCurrentPage(pageNumber)}
                        disabled={showLoading}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}

                  {/* Next Button */}
                  <button
                    className={`p-2 rounded-md ${pagination.page === pagination.pages || showLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    onClick={() => !showLoading && pagination.page < pagination.pages && setCurrentPage(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages || showLoading}
                  >
                    <ChevronRightIcon size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
            {showLoading ? (
              <div className="flex flex-col items-center justify-center">
                <RefreshCw size={24} className="animate-spin text-blue-500 mb-2" />
                <p className="text-gray-500">{t('analysisSelector.loadingAnalyses')}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <FolderOpen size={24} className="text-gray-400 mb-2" />
                <p className="text-gray-500 mb-1">{t('analysisSelector.noAnalysesAvailable')}</p>
                <p className="text-sm text-gray-400">{t('analysisSelector.createFirstAnalysis')}</p>
              </div>
            )}
          </div>
        )}
      </div>



      {/* Version History Components */}
      <VersionHistoryPanel
        isOpen={!!historyAnalysis}
        versions={historyAnalysis?.versionHistory || []}
        onClose={() => setHistoryAnalysis(null)}
        onViewSnapshot={(version) => {
          setSnapshotVersion({
            analysisId: historyAnalysis.id,
            version: version
          });
        }}
        currentVersion={historyAnalysis?.version}
      />

      <VersionSnapshotModal
        isOpen={!!snapshotVersion}
        onClose={() => setSnapshotVersion(null)}
        analysisId={snapshotVersion?.analysisId}
        version={snapshotVersion?.version}
      />

      {/* Strategic Save Modal */}
      <StrategicSaveModal
        isOpen={!!strategicAnalysis}
        onClose={() => setStrategicAnalysis(null)}
        onConfirm={(justification) => handleStrategicSave(strategicAnalysis, justification)}
        currentVersion={strategicAnalysis?.version}
      />
    </div>
  );
};

export default AnalysisSelector;
